import { useEffect, useState, useMemo } from "react";
import {
  FileTextIcon,
  BookCopyIcon,
  Loader2Icon,
  FolderOpen,
  ClockIcon,
  TrendingUpIcon,
  FileIcon,
  FolderTree,
} from "lucide-react";
import { useQuery, useMutation } from "convex/react";
import { useUser } from "@clerk/react";
import { useShortcuts } from "@/hooks/use-shortcuts";
import { useDebounce } from "@/hooks/use-debounce";
import { escapeRegExp } from "@/lib/escape-regexp";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useSearch } from "@/hooks/use-search-parms";
import { api } from "../../convex/_generated/api";
import { useAppContext } from "@/contexts/app-context";
import type { Id } from "../../convex/_generated/dataModel";

const HighlightMatch = ({ text, query }: { text: string; query: string }) => {
  if (!query.trim()) return <span>{text}</span>;

  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, "gi"));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-yellow-600/20 text-foreground font-medium">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  );
};

type SearchResultType = "document" | "course" | "studySet" | "pdf" | "subfolder";

export const Search = () => {
  const { user } = useUser();
  const { navigate } = useAppContext();
  const searchQuery = useSearch((state) => state.globalSearchQuery);
  const setSearchQuery = useSearch((state) => state.setGlobalSearchQuery);

  const toggle = useSearch((state) => state.toggle);
  const isOpen = useSearch((state) => state.isOpen);
  const onClose = useSearch((state) => state.onClose);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const trackAccess = useMutation(api.documents.trackAccess);

  // Parse page tags
  const { parsedQuery, filterType } = useMemo(() => {
    let query = debouncedSearchQuery;
    let type: "none" | "notes" | "courses" | "studySets" = "none";
    if (query.startsWith("@Notes")) {
      type = "notes";
      query = query.slice(6).trim();
    } else if (query.startsWith("@Courses")) {
      type = "courses";
      query = query.slice(8).trim();
    } else if (query.startsWith("@Study Sets")) {
      type = "studySets";
      query = query.slice(11).trim();
    }
    return { parsedQuery: query, filterType: type };
  }, [debouncedSearchQuery]);

  const searchResults = useQuery(
    api.search.getEnhancedSearch,
    isOpen
      ? {
        search: parsedQuery.trim() || undefined,
        limit: 12,
        includeContent: true,
      }
      : "skip",
  );

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  const handleCommandK = () => {
    const path = window.location.pathname;
    const isLocalPage =
      path.startsWith("/notes") ||
      path.startsWith("/courses") ||
      path.startsWith("/course") ||
      path === "/study-sets" ||
      path.startsWith("/study-sets/folder/");

    if (isLocalPage) {
      const localInput = document.getElementById("local-search-input");
      if (localInput) {
        (localInput as HTMLInputElement).focus();
        return;
      }
    }
    toggle();
  };

  useShortcuts({
    onCommandK: handleCommandK,
  });

  const documents = searchResults?.documents ?? [];
  const courses = searchResults?.courses ?? [];
  const studySets = searchResults?.studySets ?? [];
  const pdfs = searchResults?.pdfs ?? [];
  const subfolders = searchResults?.subfolders ?? [];

  const isLoading = isOpen && searchResults === undefined;
  const hasQuery = parsedQuery.trim().length > 0;
  const hasResults =
    documents.length > 0 ||
    courses.length > 0 ||
    studySets.length > 0 ||
    pdfs.length > 0 ||
    subfolders.length > 0;

  const hasFilteredResults = useMemo(() => {
    if (filterType === "none") return hasResults;
    if (filterType === "notes") return documents.length > 0;
    if (filterType === "courses") return courses.length > 0 || subfolders.length > 0 || pdfs.length > 0;
    if (filterType === "studySets") return studySets.length > 0;
    return false;
  }, [filterType, hasResults, documents, courses, subfolders, pdfs, studySets]);

  const recentDocuments = useMemo(() => {
    if (hasQuery) return [];
    return documents.filter((doc) => doc.lastAccessedAt).slice(0, 3);
  }, [documents, hasQuery]);

  const onSelect = async (id: string, type: SearchResultType, courseId?: Id<"courses">) => {
    if (type === "document") {
      trackAccess({ id: id as Id<"documents"> }).catch(console.error);
      navigate(`/document/${id}`);
      onClose();
      return;
    }

    if (type === "course") {
      navigate(`/course/${id}`);
      onClose();
      return;
    }

    if (type === "studySet") {
      navigate(`/study-sets/${id}`);
      onClose();
      return;
    }

    if (type === "pdf") {
      navigate(`/pdf/${id}`);
      onClose();
      return;
    }

    if (type === "subfolder" && courseId) {
      navigate(`/course/${courseId}/${id}`);
      onClose();
    }
  };

  if (!isMounted) return null;

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={onClose}
      className="md:min-w-[80vw] md:max-h-[85vh] lg:min-w-[60vw]"
    >
      <CommandInput
        placeholder={
          filterType === "notes"
            ? "Search Notes & PDFs..."
            : filterType === "courses"
              ? "Search Courses, Folders, and PDFs..."
              : filterType === "studySets"
                ? "Search Study Sets..."
                : `Search ${user?.firstName}'s Notes, Courses, PDFs, and Study Sets...`
        }
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList className="bg-card max-h-[min(70vh,600px)]">
        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            <Loader2Icon className="h-4 w-4 animate-spin" />
          </div>
        ) : !hasFilteredResults && !hasQuery ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Start typing to search {filterType === "notes" ? "@Notes" : filterType === "courses" ? "@Courses" : filterType === "studySets" ? "@Study Sets" : "your content"}...
          </div>
        ) : !hasFilteredResults && hasQuery ? (
          <CommandEmpty>No results found for "{parsedQuery}"</CommandEmpty>
        ) : (
          <>
            {!hasQuery && recentDocuments.length > 0 && (filterType === "none" || filterType === "notes") && (
              <CommandGroup
                heading={
                  <div className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3" />
                    <span>Recent</span>
                  </div>
                }
              >
                {recentDocuments.map((document) => (
                  <CommandItem
                    key={document._id}
                    value={`${document._id} - ${document.title}`}
                    title={document.title}
                    onSelect={() => onSelect(document._id, "document")}
                  >
                    <FileTextIcon className="mr-2 h-4 w-4 text-blue-500" />
                    <span>{document.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {hasQuery && documents.length > 0 && (filterType === "none" || filterType === "notes") && (
              <CommandGroup
                heading={
                  <div className="flex items-center gap-1">
                    <TrendingUpIcon className="h-3 w-3" />
                    <span>Notes</span>
                  </div>
                }
              >
                {documents.map((document) => (
                  <CommandItem
                    key={document._id}
                    value={`${document._id} - ${document.title}`}
                    title={document.title}
                    onSelect={() => onSelect(document._id, "document")}
                  >
                    <FileTextIcon className="mr-2 h-4 w-4 shrink-0 text-blue-500" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">
                        <HighlightMatch text={document.title} query={parsedQuery} />
                      </span>
                      {document._snippet && (
                        <span className="text-xs text-muted-foreground truncate">
                          {document._snippet}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {!hasQuery && documents.length > recentDocuments.length && (filterType === "none" || filterType === "notes") && (
              <CommandGroup heading="Notes">
                {documents.slice(recentDocuments.length).map((document) => (
                  <CommandItem
                    key={document._id}
                    value={`${document._id} - ${document.title}`}
                    title={document.title}
                    onSelect={() => onSelect(document._id, "document")}
                  >
                    <FileTextIcon className="mr-2 h-4 w-4 text-blue-600" />
                    <span>{document.title}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {pdfs.length > 0 && (filterType === "none" || filterType === "courses") && (
              <CommandGroup heading="PDFs">
                {pdfs.map((pdf) => (
                  <CommandItem
                    key={pdf._id}
                    value={`${pdf._id} - ${pdf.name}`}
                    title={pdf.name}
                    onSelect={() => onSelect(pdf._id, "pdf")}
                  >
                    <FileIcon className="mr-2 h-4 w-4 text-red-500" />
                    <span>
                      {hasQuery ? (
                        <HighlightMatch text={pdf.name} query={parsedQuery} />
                      ) : (
                        pdf.name
                      )}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {subfolders.length > 0 && (filterType === "none" || filterType === "courses") && (
              <CommandGroup heading="Folders">
                {subfolders.map((subfolder) => (
                  <CommandItem
                    key={subfolder._id}
                    value={`${subfolder._id} - ${subfolder.name}`}
                    title={subfolder.name}
                    onSelect={() => onSelect(subfolder._id, "subfolder", subfolder.courseId)}
                  >
                    <FolderTree className="mr-2 h-4 w-4 text-green-600" />
                    <span>
                      {hasQuery ? (
                        <HighlightMatch text={subfolder.name} query={parsedQuery} />
                      ) : (
                        subfolder.name
                      )}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {courses.length > 0 && (filterType === "none" || filterType === "courses") && (
              <CommandGroup heading="Courses">
                {courses.map((course) => (
                  <CommandItem
                    key={course._id}
                    value={`${course._id} - ${course.title} ${course.courseCode || ""} ${course.professorName || ""} ${course.yearSemester || ""}`}
                    title={course.title}
                    onSelect={() => onSelect(course._id, "course")}
                  >
                    <div className="flex items-center mr-2">
                      <FolderOpen className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex flex-col min-w-0 flex-1 truncate">
                      <span className="truncate">
                        {hasQuery ? (
                          <HighlightMatch text={course.title} query={parsedQuery} />
                        ) : (
                          course.title
                        )}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {course.courseCode && (
                          <span>
                            {hasQuery ? (
                              <HighlightMatch text={course.courseCode} query={parsedQuery} />
                            ) : (
                              course.courseCode
                            )}
                          </span>
                        )}
                        {course.professorName && (
                          <span>
                            {hasQuery ? (
                              <HighlightMatch text={course.professorName} query={parsedQuery} />
                            ) : (
                              course.professorName
                            )}
                          </span>
                        )}
                        {course.yearSemester && (
                          <span>
                            {hasQuery ? (
                              <HighlightMatch text={course.yearSemester} query={parsedQuery} />
                            ) : (
                              course.yearSemester
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {studySets.length > 0 && (filterType === "none" || filterType === "studySets") && (
              <CommandGroup heading="Study Sets">
                {studySets.map((studySet) => (
                  <CommandItem
                    key={studySet._id}
                    value={`${studySet._id} - ${studySet.title}`}
                    title={studySet.title}
                    onSelect={() => onSelect(studySet._id, "studySet")}
                  >
                    <BookCopyIcon className="mr-2 h-4 w-4 text-pink-500" />
                    <span>
                      {hasQuery ? (
                        <HighlightMatch text={studySet.title} query={parsedQuery} />
                      ) : (
                        studySet.title
                      )}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            <CommandSeparator />
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};
